import { horizontalDividerBlock } from '../common'
import type { ImpactSummaryType } from '~/types/types'
import { roundValue } from '~/functions/ghg'

export const handleTargetChart = (
  impactSummary: ImpactSummaryType,
  _locale: string,
  chartSvg?: string | null,
) => {

  const hasHighImpactPotential = impactSummary?.averageAnnualReduction >= 50000

  const content = [
    horizontalDividerBlock(),
    {
      text: 'Climate impact from our solutions',
      width: '*',
      style: ['blueHeader'],
    },
    {
      text: 'Our innovative solutions plan to save approximately totalGhgReduction tonnes of CO2e over the next 5 years when compared to existing products and services that provide the same function.'.replace('totalGhgReduction', roundValue(impactSummary.totalGhgReduction)),
      style: ['message'],
      width: '50%',
      margin: [0, 10, 0, 10],
    },
  ]

  return [
    ...content,
    contentSection(impactSummary, hasHighImpactPotential, chartSvg),
    {
      text: '* A company is considered high climate potential if its solutions help reduce more than 50,000 tonnes of CO2e on a yearly average over the next 5 years. This is an expert opinion based on case studies conducted by the Borderstep Institute for Innovation and Sustainability.',
      margin: [0, 0, 0, 0],
      style: ['footer'],
      width: '100%'
    },
    {
      text: '**The annual economic value is calculated by multiplying the average annual reduction by the average CO2 price in the EU Emissions Trading System from 2023 (€83.24/ton CO2e).',
      margin: [0, 10, 0, 0],
      style: ['footer'],
      width: '100%'
    },
  ]
}

const contentSection = (impactSummary: ImpactSummaryType, hasHighImpactPotential: boolean, chartSvg?: string | null) => {
  return {
    columns: [
      {
        width: "*",
        ...(chartSvg && chartSvg.trim() ? { svg: chartSvg } : {}),
        fit: [300, 180],
        margin: [20, 0, -20, 0],
      },
      {
        width: '*',
        margin: [-80, 45, 0, 0],
        stack: [
          ...[hasHighImpactPotential ? {
            margin: [0, 0, 0, 10],
            columns: [
              {
                svg: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M18 2c-.9 0-2 1-2 2H8c0-1-1.1-2-2-2H2v9c0 1 1 2 2 2h2.2c.4 2 1.7 3.7 4.8 4v2.08C8 19.54 8 22 8 22h8s0-2.46-3-2.92V17c3.1-.3 4.4-2 4.8-4H20c1 0 2-1 2-2V2zM6 11H4V4h2zm14 0h-2V4h2z" fill="#8FA5AF"/></svg>`,
                width: 15,
                height: 15,
                margin: [20, 0, -5, 0],
              },
              {
                text: 'We are a high climate potential company because our solutions have an estimated average annual reduction of averageAnnualReduction tonnes of CO2e. *'.replace('averageAnnualReduction', roundValue(impactSummary?.averageAnnualReduction ?? 0)),
                style: 'message',
              },
            ],
          } :
          {
            margin: [0, 0, 0, 10],
            columns: [
              {
                svg: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M17.9 17.39c-.26-.8-1.01-1.39-1.9-1.39h-1v-3a1 1 0 0 0-1-1H8v-2h2a1 1 0 0 0 1-1V7h2a2 2 0 0 0 2-2v-.41a7.984 7.984 0 0 1 2.9 12.8M11 19.93c-3.95-.49-7-3.85-7-7.93c0-.62.08-1.22.21-1.79L9 15v1a2 2 0 0 0 2 2m1-16A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2" fill="#8FA5AF"/></svg>`,
                width: 15,
                height: 15,
                margin: [20, 0, 0, 0],
              },
              {
                text: 'Our solutions have an estimated average annual reduction of averageAnnualReduction tonnes CO2e. *'.replace('averageAnnualReduction', roundValue(impactSummary.averageAnnualReduction)),
                style: 'message',
              },
            ],
          }],
          {
            margin: [0, 0, 0, 10],
            columns: [
              {
                svg: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M15 18.5c-2.5 0-4.68-1.42-5.76-3.5H15l1-2H8.58c-.05-.33-.08-.66-.08-1s.03-.67.08-1H15l1-2H9.24A6.49 6.49 0 0 1 15 5.5c1.61 0 3.09.59 4.23 1.57L21 5.3A8.96 8.96 0 0 0 15 3c-3.92 0-7.24 2.5-8.5 6H3l-1 2h4.06c-.06.33-.06.66-.06 1s0 .67.06 1H3l-1 2h4.5c1.26 3.5 4.58 6 8.5 6c2.31 0 4.41-.87 6-2.3l-1.78-1.77c-1.13.98-2.6 1.57-4.22 1.57" fill="#8FA5AF"/></svg>`,
                width: 15,
                height: 15,
                margin: [20, 0, 0, 0],
              },
              {
                text: 'Our annual economic value of the planned GHG reduction is economicValue € **'.replace('economicValue', roundValue(impactSummary.economicValue)),
                style: 'message',
              },
            ],
          },
          ...[(impactSummary?.impactSolutionSummaries || [])
            .map((solutionSummary) => {
              if (
                solutionSummary?.totalGhgReduction === 'N/A'
                || solutionSummary?.totalGhgReduction === null
                || solutionSummary?.totalGhgReduction === undefined
                || solutionSummary?.totalGhgReduction === ''
              ) {
                return {}
              }
              return {
                margin: [0, 0, 0, 10],
                columns: [
                  {
                    svg: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M20 11h3v2h-3zM1 11h3v2H1zM13 1v3h-2V1zM4.92 3.5l2.13 2.14l-1.42 1.41L3.5 4.93zm12.03 2.13l2.12-2.13l1.43 1.43l-2.13 2.12zM12 6a6 6 0 0 1 6 6c0 2.22-1.21 4.16-3 5.2V19a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-1.8c-1.79-1.04-3-2.98-3-5.2a6 6 0 0 1 6-6m2 15v1a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1zm-3-3h2v-2.13c1.73-.44 3-2.01 3-3.87a4 4 0 0 0-4-4a4 4 0 0 0-4 4c0 1.86 1.27 3.43 3 3.87z" fill="#8FA5AF"/></svg>`,
                    width: 15,
                    height: 15,
                    margin: [20, 0, 0, 0],
                  },
                  {
                    text: `Our solution ${solutionSummary.impactSolution?.concept?.label ?? 'N/A'} plans to save ${roundValue(solutionSummary.totalGhgReduction ? Number(solutionSummary.totalGhgReduction) : null)} tonnes of CO2e from ${solutionSummary.startYear} to ${solutionSummary.endYear}, by reducing GHG emissions by ${roundValue(solutionSummary.impactSolution?.ghgSavingPotential?.specificReductionAsPercentage)}% per ${solutionSummary.impactSolution?.ghgSavingPotential?.comparativeUnit} (${roundValue(solutionSummary.impactSolution?.ghgSavingPotential?.specificReductionPerUnit)} kg CO2e).`,
                    style: 'message',
                  },
                ],
              }
          })],
        ],
      },
    ],
    alignment: 'top'
  }
}
